import { useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Check, Star, Asterisk, Volume2, VolumeX } from "lucide-react";
import { SITE, WA_LINK } from "@/lib/site";
import { mediaUrl } from "@/lib/api";

export function Hero({ videos }) {
  const safeVideos = Array.isArray(videos) ? videos : [];
const featured = safeVideos.find((v) => v.format === "vertical") || safeVideos[0];
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 80]);
  const [soundOn, setSoundOn] = useState(false);
  const videoRef = useRef(null);

  const toggleSound = () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = soundOn;
    if (el.paused) el.play().catch(() => {});
    setSoundOn(!soundOn);
  };

  return (
    <section id="top" className="pt-24 md:pt-40 pb-10 md:pb-16 overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-5 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-end">
        <div className="lg:col-span-7">
          <motion.p
            className="eyebrow text-ink-soft mb-8 flex items-center gap-2"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="hero-eyebrow"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-ink inline-block" />
            {SITE.eyebrow}
          </motion.p>
          <motion.h1
            className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-ink"
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="hero-headline"
          >
            {SITE.headlineLead}
            <br />
            <span className="text-ink/40">{SITE.headlineMuted}</span>
          </motion.h1>
          <motion.p
            className="mt-8 text-base sm:text-lg leading-relaxed text-ink-soft max-w-md"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.22 }}
            data-testid="hero-subheadline"
          >
            {SITE.subheadline}
          </motion.p>
          <motion.div
            className="mt-8 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.34 }}
          >
            <motion.a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-call"
              whileTap={{ scale: 0.96 }}
              className="inline-flex items-center justify-center gap-2 bg-ink text-cream px-7 py-4 sm:py-3.5 rounded-full text-xs uppercase tracking-[0.14em] font-semibold hover:bg-neutral-800 transition-colors duration-300 w-full sm:w-auto"
            >
              Work With Me <ArrowUpRight className="w-4 h-4" />
            </motion.a>
            <motion.a
              href="#work"
              data-testid="hero-cta-work"
              whileTap={{ scale: 0.96 }}
              className="inline-flex items-center justify-center gap-2 border border-ink/20 text-ink px-7 py-4 sm:py-3.5 rounded-full text-xs uppercase tracking-[0.14em] font-semibold hover:border-ink transition-colors duration-300 w-full sm:w-auto"
            >
              View My Work <ArrowDown className="w-4 h-4" />
            </motion.a>
          </motion.div>
          <motion.div
            className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.42 }}
            data-testid="hero-proof"
          >
            <span className="flex items-center gap-0.5 shrink-0" aria-label="5 out of 5 stars">
              {[...Array(5)].map((_, s) => (
                <Star key={s} className="w-4 h-4 fill-ink text-ink" />
              ))}
            </span>
            <span className="text-sm font-semibold text-ink">{SITE.trustLine}</span>
            <span className="text-ink/30 hidden sm:inline">·</span>
            <span className="inline-flex items-center gap-1.5 text-sm text-ink-soft" data-testid="hero-booking-status">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block animate-pulse" />
              Booking {new Date().toLocaleString("en-IN", { month: "long" })} projects
            </span>
          </motion.div>
          <motion.ul
            className="mt-6 flex flex-wrap gap-x-6 gap-y-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            data-testid="hero-proof-chips"
          >
            {SITE.proofChips.map((c) => (
              <li key={c} className="flex items-center gap-2 text-sm text-ink-soft">
                <Check className="w-4 h-4 text-ink" strokeWidth={2.5} /> {c}
              </li>
            ))}
          </motion.ul>
          <motion.div
            className="mt-12 flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] uppercase tracking-[0.2em] text-ink-soft/80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            data-testid="hero-tags"
          >
            {SITE.heroTags.map((t, i) => (
              <span key={t} className="flex items-center gap-3">
                {i > 0 && <span className="text-ink/25">·</span>}
                {t}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div
          className="lg:col-span-5 lg:justify-self-end w-full max-w-[340px] sm:max-w-[400px] mx-auto lg:mx-0 relative mt-6 lg:mt-0"
          style={{ y }}
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.2 }}
        >
          <motion.span
            className="absolute -top-8 -right-3 text-ink/10 pointer-events-none select-none"
            animate={{ rotate: 360 }}
            transition={{ duration: 26, repeat: Infinity, ease: "linear" }}
            aria-hidden="true"
          >
            <Asterisk className="w-16 h-16 md:w-20 md:h-20" />
          </motion.span>
          {featured ? (
            <div className="relative rounded-[2rem] overflow-hidden bg-cream-dark shadow-2xl shadow-ink/10" data-testid="hero-video-frame">
              <video
                ref={videoRef}
                src={mediaUrl(featured.video_url)}
                poster={mediaUrl(featured.thumbnail_url)}
                className="w-full aspect-[9/16] object-cover grayscale"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                data-testid="hero-featured-video"
              />
              <span className="absolute top-4 right-3 text-[10px] uppercase tracking-[0.3em] text-white/70 [writing-mode:vertical-rl]">
                {SITE.name} / {new Date().getFullYear()}
              </span>
              <button
                onClick={toggleSound}
                data-testid="hero-sound-toggle"
                aria-label={soundOn ? "Mute video" : "Play video with sound"}
                className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-cream/95 flex items-center justify-center text-ink shadow-lg active:scale-90 transition-transform"
              >
                {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
              <div className="absolute bottom-4 left-4 bg-cream rounded-xl px-4 py-3 shadow-lg max-w-[200px]" data-testid="hero-availability-badge">
                <p className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.16em] font-semibold text-ink-soft">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                  {SITE.availabilityBadge}
                </p>
                <p className="mt-1 font-serif text-base leading-snug text-ink">{SITE.availabilityLine}</p>
              </div>
            </div>
          ) : (
            <div className="aspect-[9/16] rounded-[2rem] bg-cream-dark animate-pulse" data-testid="hero-video-skeleton" />
          )}
          <motion.div
            className="absolute -left-3 top-10 bg-ink text-cream rounded-xl px-3.5 py-2.5 shadow-xl pointer-events-none"
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
            data-testid="hero-float-rating"
          >
            <p className="flex items-center gap-1">
              {[...Array(5)].map((_, s) => <Star key={s} className="w-2.5 h-2.5 fill-cream text-cream" />)}
            </p>
            <p className="mt-1 text-[9px] uppercase tracking-[0.14em] text-cream/80">Rated 5.0</p>
          </motion.div>
          <motion.div
            className="absolute -right-3 top-1/3 bg-cream border border-cream-dark rounded-xl px-3.5 py-2.5 shadow-xl pointer-events-none"
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
            data-testid="hero-float-views"
          >
            <p className="font-serif text-lg leading-none text-ink">12M+</p>
            <p className="mt-1 text-[9px] uppercase tracking-[0.14em] text-ink-soft">Views generated</p>
          </motion.div>
        </motion.div>
      </div>

      {safeVideos.length > 1 && (
        <motion.a
          href="#work"
          data-testid="hero-work-strip"
          className="mt-16 md:mt-24 block border-y border-cream-dark py-4 overflow-hidden group"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.7 }}
          aria-label="See all work"
        >
          <div className="marquee-track flex gap-4 w-max">
          {[...safeVideos, ...safeVideos].map((v, i) => (
              <div key={`${v.id}-${i}`} className="relative shrink-0 overflow-hidden rounded-xl">
                <img
                  src={mediaUrl(v.thumbnail_url)}
                  alt={v.title}
                  loading="lazy"
                  className={`object-cover bg-cream-dark grayscale group-hover:grayscale-0 transition-[filter] duration-500 ${v.format === "vertical" ? "h-32 md:h-44 aspect-[9/16]" : "h-32 md:h-44 aspect-video"}`}
                />
                <span className="absolute bottom-2 left-2 bg-cream/95 text-ink text-[9px] uppercase tracking-[0.14em] font-semibold px-2 py-1 rounded-full">
                  {v.category}
                </span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-center text-[10px] uppercase tracking-[0.24em] text-ink-soft/70 group-hover:text-ink transition-colors">
            Tap to explore selected work ↓
          </p>
        </motion.a>
      )}
    </section>
  );
}
